import { MutableRefObject, useRef } from 'react';
import { createPortal } from 'react-dom';

import { AccountFooter, Button, Icon } from '@components';
import useOnClickOutside from '@utils/hooks';
import { Org } from 'types/organization';

import {
  trackDynamicComponentCta,
  trackDynamicComponentDismissed
} from '../../analytics/dynamicComponentMixpanel';
import {
  ComponentType,
  PopupData,
  PopupResolvedStyles,
  ProcessedComponentInstance
} from '../../types';
import sanitizeHtml from '../../utils/sanitizeHtml';

interface AccountFooterConfig {
  orgList: Org[];
  onSwitchAccount?: () => void;
  onBeforeCreateAccount?: () => void;
}

interface DynamicPopupProps {
  instance: ProcessedComponentInstance<PopupData, PopupResolvedStyles>;
  showPrimary: boolean;
  showSecondary: boolean;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  onClose: () => void;
  accountFooter: AccountFooterConfig;
}

const DynamicPopup = ({
  instance,
  showPrimary,
  showSecondary,
  onPrimaryClick,
  onSecondaryClick,
  onClose,
  accountFooter
}: DynamicPopupProps) => {
  const popupRef = useRef() as MutableRefObject<HTMLDivElement>;

  const { data, resolvedStyles } = instance;
  const isDismissible = instance.is_dismissible;
  const { title, description } = data;
  const primaryButton = data.primary_button;
  const secondaryButton = data.secondary_button;
  const { bgColor, titleColor, descriptionColor, buttonBgColor } =
    resolvedStyles;
  const imageUrl = resolvedStyles.image_url;

  const overlayClass = isDismissible ? 'bg-black/20' : 'bg-black/50';

  // Popups without a CTA (e.g. the churn alert) render title + description only.
  const primaryCta = showPrimary && onPrimaryClick ? primaryButton : undefined;
  const secondaryCta =
    showSecondary && onSecondaryClick ? secondaryButton : undefined;
  const hasButtons = !!primaryCta || !!secondaryCta;

  const trackDismiss = () => {
    trackDynamicComponentDismissed(
      instance,
      ComponentType.POPUP,
      'Popup close button'
    );
  };

  const handleClose = () => {
    trackDismiss();
    onClose();
  };

  const handlePrimaryClick = () => {
    if (!primaryButton) return;
    trackDynamicComponentCta(primaryButton, instance, ComponentType.POPUP);
    onPrimaryClick?.();
  };

  const handleSecondaryClick = () => {
    if (secondaryButton) {
      trackDynamicComponentCta(secondaryButton, instance, ComponentType.POPUP);
    }
    if (isDismissible) trackDismiss();
    onSecondaryClick?.();
  };

  useOnClickOutside(popupRef, (e) => {
    if (isDismissible) {
      e.stopPropagation();
      handleClose();
    }
  });

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div className={`absolute size-full ${overlayClass}`} />
      <div
        ref={popupRef}
        className="relative mx-4 w-full max-w-md overflow-hidden rounded-lg shadow-xl"
        style={{ backgroundColor: bgColor, maxWidth: 538 }}
      >
        {isDismissible && (
          <Button
            variant="transparent"
            handleClick={handleClose}
            className="absolute right-3 top-3 z-10 !p-0.5"
            aria-label="Close popup"
          >
            <Icon name="close" size="mediumlarge" />
          </Button>
        )}

        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            className="w-full object-cover"
            style={{ height: 164 }}
          />
        )}

        <div className="px-6 pb-6 pt-5">
          <h2
            className="mb-4 text-center text-28 font-semibold"
            style={{ color: titleColor }}
          >
            {title}
          </h2>

          <div
            className={`text-center text-xl ${hasButtons ? 'mb-8' : ''}`}
            style={{ color: descriptionColor }}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(description)
            }}
          />

          {hasButtons && (
            <div
              className="flex flex-col gap-2"
              style={
                {
                  '--btn-bg': buttonBgColor
                } as React.CSSProperties
              }
            >
              {primaryCta && (
                <Button
                  variant="primary"
                  size="full"
                  label={primaryCta.text}
                  handleClick={handlePrimaryClick}
                  className="h-12.5 !bg-[var(--btn-bg)]"
                  labelStyle="!text-theme"
                />
              )}

              {secondaryCta && (
                <Button
                  variant="transparent"
                  size="full"
                  label={secondaryCta.text}
                  handleClick={handleSecondaryClick}
                  className="h-12.5"
                  labelStyle="!text-[var(--btn-bg)]"
                />
              )}
            </div>
          )}
        </div>

        {!isDismissible && <AccountFooter {...accountFooter} />}
      </div>
    </div>,
    document.body
  );
};

export default DynamicPopup;
